import { useCallback, useMemo } from 'react';
import { Checkbox, Stack, Text, TooltipHost, useTheme } from '@fluentui/react';
import { useRowMetaContext } from '../../../context/RowMetaContext';
import { StateDropdown } from './components/StateDropdown';
import { ActivityDropdown } from './components/ActivityDropdown';
import { getLocaleFormattedDate } from '../../../../../../../_helpers/getLocaleFormattedDate';
import { UserReportingPeriodState } from '../../../../../../../api/generated/data-contracts';
import { TimeEntryApprovalDtoState, useCurrencyRate, useSelectedRowIdState } from '../../../../../../../_grid/grid3';
import { useGridLayoutContext } from '../../../context/LayoutProvider';
import { useTimeUIConfig } from '../../../../../../../hooks/queries/useTimeUIConfig';
import { HoursApprovedColumn } from './components/HoursApprovedColumn';

export const ApprovalRow = ({ row }: { row: TimeEntryApprovalDtoState }) => {
    const { gridRowStyles } = useGridLayoutContext();
    const { config } = useTimeUIConfig();
    return (
        <div style={gridRowStyles}>
            <RowHeader />
            <HoursColumn />
            <HoursApprovedColumn />
            <DateColumn />
            {config?.approval?.projectManagerApproval?.useRates && <RateColumn />}
            {config?.approval?.projectManagerApproval?.useRates && <ActivityDropdown />}
            <StateDropdown />
            <CommentColumn />
        </div>
    );
};

const RowHeader = () => {
    const { row } = useRowMetaContext<TimeEntryApprovalDtoState>();
    const { nestingLevel } = useRowMetaContext();
    const [selected, setSelected] = useSelectedRowIdState(row.id);

    const onChange = useCallback(
        (e: any, newValue?: boolean) => {
            setSelected(Boolean(newValue));
        },
        [setSelected],
    );
    return (
        <Stack style={{ paddingLeft: 20 * nestingLevel + 50 }} horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
            <Checkbox checked={selected} onChange={onChange} />
            <div style={{ overflow: 'hidden' }}>
                <Text nowrap>{row.name}</Text>
            </div>
        </Stack>
    );
};

const RateColumn = () => {
    const { row } = useRowMetaContext<TimeEntryApprovalDtoState>();
    const rate = useCurrencyRate(row.id);
    return (
        <Stack>
            <Text>{rate}</Text>
        </Stack>
    );
};

const HoursColumn = () => {
    const { row } = useRowMetaContext<TimeEntryApprovalDtoState>();
    const hours = useMemo(() => row.timeEntry.hours.toNumber(), [row.timeEntry.hours]);
    return (
        <Stack>
            <Text>{hours}</Text>
        </Stack>
    );
};

const DateColumn = () => {
    const { row } = useRowMetaContext<TimeEntryApprovalDtoState>();
    const date = useMemo(() => {
        return getLocaleFormattedDate(row.timeEntry.date);
    }, [row.timeEntry.date]);

    return (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
            <Text>{date}</Text> <UserPeriod userPeriodState={row.userPeriodState} />
        </Stack>
    );
};

const UserPeriod = ({ userPeriodState }: { userPeriodState: UserReportingPeriodState }) => {
    const theme = useTheme();
    const { color, text } = useMemo(() => {
        switch (userPeriodState) {
            case 'Open':
            case 'Submitted':
                return {
                    color: theme.palette.yellow,
                    text: 'This time registration awaits approval by the manager.',
                };
            case 'Approved':
                return {
                    color: theme.palette.green,
                    text: 'This time registration has been approved by the manager.',
                };
            case 'Rejected':
            default:
                return {
                    color: theme.palette.red,
                    text: 'This time registration has been rejected by the manager.',
                };
        }
    }, [theme.palette.green, theme.palette.red, theme.palette.yellow, userPeriodState]);
    return (
        <TooltipHost content={text}>
            <div style={{ cursor: 'help', userSelect: 'none', backgroundColor: color, height: '1rem', width: '1rem', borderRadius: '100%' }} />
        </TooltipHost>
    );
};

const CommentColumn = () => {
    const { row } = useRowMetaContext<TimeEntryApprovalDtoState>();
    const comment = row.timeEntry.comment || '';
    const maxLength = 50;
    const truncatedComment = comment.length > maxLength ? comment.substring(0, maxLength) + '...' : comment;

    return (
        <Stack>
            {comment && (
                <TooltipHost content={comment} styles={{ root: { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}>
                    <Text style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{truncatedComment}</Text>
                </TooltipHost>
            )}
        </Stack>
    );
};
